import { useCallback, useEffect, useRef } from 'react'
import ReactOnRails from 'react-on-rails'
import { useApp } from '~/context/appContext'
import { ANNOUNCEMENT_EVENTS, recordAnnouncementEvent } from '~/service/Announcements/fetchApiClient'

// Reporting for every placement. Always resolves - a preview or a missing url/token is a
// no-op and failures are swallowed - so callers can still .then() a navigation onto it.
//
// viewOnMount is opt-in because the event is not free: it is a modal's suppression gate
// (RecordInteraction#record_view is the only thing that moves views_count), while a top bar
// is ambient and retires on dismissals instead.
export const useAnnouncementEvents = ({ eventsUrl, renderToken, preview = false, viewOnMount = false }) => {
  const { csrf: contextCsrf } = useApp()

  const report = useCallback((event) => {
    if (preview || !eventsUrl || !renderToken) return Promise.resolve(null)

    return recordAnnouncementEvent({
      eventsUrl,
      renderToken,
      event,
      // Fallback rather than default: authenticityToken() reads `document`, absent on the
      // server render the layout mount goes through.
      csrf: contextCsrf || ReactOnRails.authenticityToken(),
    }).catch(() => null)
  }, [preview, eventsUrl, renderToken, contextCsrf])

  // Keyed on the token so a genuinely new render still counts, while a StrictMode double
  // mount does not spend a round trip to have the server dedupe it.
  const viewedToken = useRef(null)

  useEffect(() => {
    if (!viewOnMount || preview || !eventsUrl || !renderToken) return
    if (viewedToken.current === renderToken) return

    viewedToken.current = renderToken
    report(ANNOUNCEMENT_EVENTS.VIEWED)
  }, [viewOnMount, preview, eventsUrl, renderToken, report])

  return report
}
